import * as React from "react";
import { Button, Divider, Form, Grid, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { FileAnswer } from "../../questionnaires/model";
import { FileUpload } from "../../fileUploads";
import { acceptableExtensions } from "../../acceptableExtensions";
import { toast } from "../../toasts";
import { ShareDocumentsMessageDto } from "../models";

interface Props {
    employeeRecordId: string;
    fileApiUrl: string;
    onSubmit: (model: ShareDocumentsMessageDto) => Promise<void>;
}

export const SendDocumentsToAssociate: React.FunctionComponent<Props> = (props: Props) => {

    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [proceed, setProceed] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>(null);
    const [documents, setDocuments] = React.useState<FileAnswer[]>([]);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);

    React.useEffect(() => {
        return resetState;
    }, []);

    const onSendDocumentsClick = () => setModalOpen(true);

    const onProceedChange = (checked: boolean) => setProceed(checked);

    const onMessageChange = (newMessage: string) => setMessage(newMessage);

    const changeDocument = (index: number, blobId: string, fileName: string) => {
        setDocuments([
            ...documents.slice(0, index),
            { blobId, fileName },
            ...documents.slice(index + 1)
        ]);
    };

    const removeDocument = (index: number) => {
        setDocuments([
            ...documents.slice(0, index),
            ...documents.slice(index + 1)
        ]);
    };

    const addDocument = () => {
        setDocuments([
            ...documents,
            { blobId: null, fileName: null }
        ]);
    };

    const onCancel = () => {
        resetState();
    };

    const onSendDocumentsConfirm = async () => {
        setSubmitted(true);

        const noDocuments = documents?.filter(d => d.blobId)?.length < 1;
        if (!proceed || !message || noDocuments) {
            if (noDocuments) {
                toast.warning("No documents have been uploaded.");
            }

            return;
        }

        const dto: ShareDocumentsMessageDto = {
            employeeRecordId: props.employeeRecordId,
            message,
            documents,
            acceptedByTheAssociate: false
        };
        setSubmitting(true);
        await props.onSubmit(dto);
        setSubmitting(false);
        toast.success("The documents have been sent.");
        resetState();
    };

    const resetState = () => {
        setModalOpen(false);
        setProceed(false);
        setMessage(null);
        setDocuments([]);
        setSubmitted(false);
        setSubmitting(false);
    };

    return (
        <>
            <Modal open={modalOpen}>
                <Modal.Header content="Send Documents To Associate" />
                <Modal.Content>
                    <Form>
                        <p>
                            You have chosen to send a message to the associate. By completing this form the associate will be sent
                            a message and will be asked to log in and confirm they have read and understood the message.
                        </p>
                        <Input.Checkbox
                            required
                            value={proceed}
                            showErrors={submitted}
                            label="Please tick here to proceed"
                            onChange={onProceedChange}
                        />
                        <Divider hidden />
                        {proceed &&
                            <>
                                <Input.Textarea
                                    required
                                    value={message}
                                    showErrors={submitted}
                                    label="Type Message Here"
                                    inputLabelPosition="left corner"
                                    onChange={onMessageChange}
                                />
                                <Divider hidden />
                                <Form.Field>
                                    {documents && documents.map((document, index) =>
                                        (<React.Fragment key={index}>
                                            <Grid columns="2">
                                                <Grid.Column>
                                                    {document.blobId
                                                        ? <a
                                                            key={index}
                                                            href={`${props.fileApiUrl}/${document.blobId}?fileName=${document.fileName}`}
                                                        >
                                                            <Button content={document.fileName} type="button" />
                                                        </a>
                                                        : <FileUpload
                                                            apiUrl={props.fileApiUrl}
                                                            onFileChange={(blobId, fileName) => changeDocument(index, blobId, fileName)}
                                                            fileName={document.fileName}
                                                            acceptExtensions={acceptableExtensions}
                                                        />}
                                                </Grid.Column>
                                                <Grid.Column textAlign="right" verticalAlign="middle">
                                                    <Button
                                                        className="remove-evidence-button"
                                                        content="Remove"
                                                        type="button"
                                                        onClick={() => removeDocument(index)}
                                                        floated="right"
                                                    />
                                                </Grid.Column>
                                            </Grid>
                                            <Divider />
                                        </React.Fragment>))
                                    }
                                    <Button content="Attach Documents" icon="add" onClick={addDocument} />
                                </Form.Field>
                            </>
                        }
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onCancel} />
                    <Button content="Send" onClick={onSendDocumentsConfirm} loading={submitting} />
                </Modal.Actions>
            </Modal>
            <Button content="Send Documents To Associate" icon="file" onClick={onSendDocumentsClick} />
        </>
    );
};
